import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getLtvDetail, exportCsv } from 'services/api/AdebifyApi';
import { TAB_AD } from 'store/settings/constant';
import { normalizeLtvDetailApiDimensions } from 'views/pages/adebify/ltv-detail/components/dimensionsCatalog';
import commonSelectors from 'store/common/selectors';
import { commonActions } from 'store/common';
import settingsSelector from 'store/settings/selectors';
import types from './types';
import actions from './actions';
import selectors from './selectors';

function* fetchLtvDetail() {
  try {
    yield put(actions.requestStart());

    const period = yield select(commonSelectors.periodSelector);
    const tab = yield select(settingsSelector.getTab);
    const ltvEvaluationPeriod = yield select(selectors.getLtvEvaluationPeriod);
    const pagination = yield select(selectors.getPagination);
    const sort = yield select(selectors.getSort);
    const axis = yield select(selectors.getAxis);
    const productAxis = yield select(selectors.getProductAxis);
    const rawDimensions = yield select(selectors.getDimensions);
    const metrics = yield select(selectors.getMetrics);
    const filters = yield select(selectors.getFilters);

    const startDate = period?.start?.format('YYYY-MM-DD') || '';
    const endDate = period?.end?.format('YYYY-MM-DD') || '';

    if (!startDate || !endDate) return;

    const channel = tab;
    const offset = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const dimensions = normalizeLtvDetailApiDimensions(
      rawDimensions,
      productAxis,
      tab === TAB_AD
    );

    const params = {
      start_date: startDate,
      end_date: endDate,
      channel,
      ltv_evaluation_period: ltvEvaluationPeriod,
      axis,
      product_axis: productAxis,
      dimensions,
      metrics,
      sort: `${sort.direction === 'desc' ? '-' : ''}${sort.field}`,
      limit: pagination.itemsPerPage,
      offset,
    };

    // Pre-mapped to f1_* / snake_case API keys by the container; only emit
    // the field when at least one filter is active.
    if (filters && Object.keys(filters).length > 0) {
      params.filters = filters;
    }

    const response = yield call(getLtvDetail, params);
    const result = response.data;

    yield put(
      actions.fetchLtvDetailSucceeded({
        summary: result.summary || {},
        tableData: result.data || result.table_data || [],
        graphData: result.graph_data || [],
        meta: result.meta || { total_count: 0 },
      })
    );
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || error.message || 'データの取得に失敗しました';
    yield put(actions.fetchLtvDetailFailed(errorMessage));
  }
}

function* handleDownloadCsv() {
  try {
    yield put(commonActions.setDownloadNotify('CSVファイルを作成しています...'));

    const period = yield select(commonSelectors.periodSelector);
    const tab = yield select(settingsSelector.getTab);
    const channel = tab;
    const ltvEvaluationPeriod = yield select(selectors.getLtvEvaluationPeriod);
    const axis = yield select(selectors.getAxis);
    const productAxis = yield select(selectors.getProductAxis);
    const rawDimensions = yield select(selectors.getDimensions);
    const metrics = yield select(selectors.getMetrics);
    const filters = yield select(selectors.getFilters);

    const startDate = period?.start?.format('YYYY-MM-DD') || '';
    const endDate = period?.end?.format('YYYY-MM-DD') || '';
    const dimensions = normalizeLtvDetailApiDimensions(
      rawDimensions,
      productAxis,
      channel === TAB_AD
    );

    const exportParams = {
      report_type: 'ltv_detail',
      start_date: startDate,
      end_date: endDate,
      ltv_evaluation_period: ltvEvaluationPeriod,
      channel,
      axis,
      product_axis: productAxis,
      dimensions,
      metrics,
    };
    if (filters && Object.keys(filters).length > 0) {
      exportParams.filters = filters;
    }

    const response = yield call(exportCsv, exportParams);

    // Response contains a presigned S3 download URL
    const downloadUrl = response.data?.download_url;
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    yield put(commonActions.setDownloadNotify('CSVファイルを作成しました'));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('CSV export failed:', error);
    yield put(commonActions.setDownloadNotify(''));
  }
}

// Exported for saga payload contract tests.
export { fetchLtvDetail, handleDownloadCsv };

export default function* adebifyLtvDetailOperations() {
  yield takeLatest(types.FETCH_LTV_DETAIL, fetchLtvDetail);
  yield takeLatest(types.SET_LTV_EVALUATION_PERIOD, fetchLtvDetail);
  yield takeLatest(types.UPDATE_SORT, fetchLtvDetail);
  yield takeLatest(types.UPDATE_PAGE, fetchLtvDetail);
  yield takeLatest(types.SET_AXIS, fetchLtvDetail);
  yield takeLatest(types.SET_PRODUCT_AXIS, fetchLtvDetail);
  yield takeLatest(types.SET_DIMENSIONS, fetchLtvDetail);
  yield takeLatest(types.SET_METRICS, fetchLtvDetail);
  yield takeLatest(types.SET_FILTERS, fetchLtvDetail);
  // Atomic update from the 項目切替 modal — single re-fetch instead of one
  // per field.
  yield takeLatest(types.SET_DISPLAY_ITEMS, fetchLtvDetail);
  yield takeLatest(types.DOWNLOAD_CSV, handleDownloadCsv);
}
